import { Grid as UikGrid } from '@appd/uik';
import { HealthIconCellRendererProps } from '../../health/HealthIconCellRenderer';
import { ECPLinkRendererProps } from '../link/ECPLinkRenderer';
import { SparklineCellRendererProps } from '../cartesian/SparklineCellRenderer';

export type ObjectType = Record<string, unknown>;

export const CellType = {
  NUMBER: 'number',
  STRING: 'string',
  BOOLEAN: 'boolean',
  TIMESTAMP: 'timestamp', // timestamp in ISO format
  DURATION: 'duration', // ISO 8601 duration
  TIMESERIES: 'timeseries', // array of objects containing a timestamp field
  ARRAY: 'array',
  OBJECT: 'object',
  NULL: 'null',
  UNKNOWN: 'unknown',
  HEALTH: 'health',
  ECP_LINK: 'ecpLink',
} as const;
export type CellTypeOption = (typeof CellType)[keyof typeof CellType];

export type getRowsParamsType = UikGrid.Props.serverSideDatasource.getRows.params<any>;

export type Rows = UikGrid.Props<any>['rows'];

export type FormatAs = {
  type?: string; // "number.short"
  options?: Record<string, unknown>;
};

export type ValueFormatterOptions = {
  enabled?: boolean;
  formatAs?: FormatAs;
};

export interface ColumnMeta<TData, TValue> {
  cellType?: CellTypeOption; // TODO: make this required after DataGrid is depecrated
  align?: ContentAlignmentOption;
  valueFormatterOptions?: ValueFormatterOptions;
  cellTypeOptions?:
    | Omit<HealthIconCellRendererProps, 'value'>
    | Omit<ECPLinkRendererProps, 'id' | 'value'>
    | Omit<SparklineCellRendererProps, 'data' | 'summaryValue'>;
}
export interface TableMeta {
  enableValueFormatting?: boolean;
}

// TODO: cell context depending on AG grid cell api
export type CellContext<TData, TValue> = number | string | boolean | ObjectType | ObjectType[];
export type CellRendererParams<TData, TValue> = UikGrid.CellRendererParams<TData, TValue>;

export interface useRowSourceParamsType {
  data: any[];

  // props for server-side pagination model
  fetchNextPage?: () => void;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
}

export interface useRowSourceReturnType {
  rows: Rows;
  serverSideInitialRowCount?: number;
}

export type ColumnDef<TData, TValue = unknown> = UikGrid.Props.column<Record<string, any>> & ColumnMeta<TData, TValue>;
export type ColumnOverrides<TData> = Record<string, Partial<ColumnDef<TData>>>;

export interface GridProps<TData> extends useRowSourceParamsType {
  // tableOptions, columnOverrides etc
  columnOverrides?: ColumnOverrides<TData>;
  tableOptions?: Partial<TableMeta>;
  metadata?: Record<string, unknown>;
}

export type TableColumn<TData, TValue = unknown> = Partial<ColumnDef<TData, TValue>> & {
  colId: string;
};

/** STYLING **/
export const ContentAlignment = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;
export type ContentAlignmentOption = (typeof ContentAlignment)[keyof typeof ContentAlignment];
